import styled, { useTheme } from 'styled-components';
import { useState } from 'react';
import { Dropdown, Space, theme as antdTheme, Typography } from 'antd';
import Title from '@copilot/common/typography/title';
import { BentoMenuIcon } from '@copilot/common/components/icon/bentoMenuIcon';
import styles from './header.module.less';

const { useToken } = antdTheme;

const StyledMenu = styled.div`
	.${(props) => props.theme['@ant-prefix']}-dropdown-menu {
		padding: ${(props) => props.theme['@spacer-size-sm']};
		li:not(:last-child) {
			margin-bottom: 8px;
		}
	}
	.${(props) => props.theme['@ant-prefix']}-dropdown-menu-item {
		padding: 8px !important;
	}
	.${(props) => props.theme['@ant-prefix']}-dropdown-menu-item-selected {
		&:hover {
			cursor: auto !important;
		}
	}
`;

type ProductSwitcherPropsType = {
	/**
	 * Icon color
	 */
	color?: string;
	/**
	 * Icon color on focused
	 */
	focusColor?: string;
	/**
	 * Selected key
	 */
	currentProductKey: ValueOf<typeof ProductKeys>;
	/**
	 * Whether the product switcher should be styled for dark or light mode
	 */
	isDark?: boolean;
};

export const ProductKeys = {
	accountDashboard: 'accountDashboard',
	teamsAccount: 'teamsAccount',
} as const;

/**
 * Helper to open a link in a new tab
 */
function openLinkInNewTab(link: string) {
	window.open(link, '_blank');
}

/**
 * Renders a product switcher dropdown menu.
 *
 * @constructor
 * @returns {JSX.Element} The product switcher dropdown menu.
 */
export function ProductSwitcher(props: ProductSwitcherPropsType): JSX.Element {
	const theme = useTheme();
	const {
		color = theme['@Gray/7'],
		focusColor = theme['@info-color'],
		currentProductKey,
		isDark,
	} = props;
	const { token } = useToken();
	const [focus, setFocus] = useState<boolean>(false);

	return (
		<Dropdown
			trigger={['click']}
			menu={{
				items: [
					{
						key: ProductKeys.accountDashboard,
						label: (
							<Space direction={'vertical'} size={theme['@spacer-num-xxs']}>
								<Title level={5}>Accounts Dashboard</Title>
								<Typography.Text style={{ fontSize: token.fontSizeHeading5 }}>
									Monitor and manage customers' campaigns
								</Typography.Text>
							</Space>
						),
						onClick: ({ key }) => {
							if (currentProductKey === key) return;
							openLinkInNewTab('/dashboard');
						},
					},
					{
						key: ProductKeys.teamsAccount,
						label: (
							<Space direction={'vertical'} size={theme['@spacer-num-xxs']}>
								<Title level={5}>Your Team's Account</Title>
								<Typography.Text style={{ fontSize: token.fontSizeHeading5 }}>
									Create your own campaigns to generate leads for your company
								</Typography.Text>
							</Space>
						),
						onClick: ({ key }) => {
							if (currentProductKey === key) return;
							// TODO [COPILOT-7099] redirect user to the Your team's account page in account dashboard
							openLinkInNewTab('/');
						},
					},
				],
				selectable: true,
				selectedKeys: [currentProductKey],
				onClick: () => {
					setFocus(false);
				},
			}}
			placement="bottomRight"
			onOpenChange={setFocus}
			overlayStyle={{ width: 260, userSelect: 'none' }}
			dropdownRender={(menus) => <StyledMenu>{menus}</StyledMenu>}
		>
			<div className={isDark ? styles.productSwitcherDark : styles.productSwitcherLight} tabIndex={1}>
				<BentoMenuIcon color={focus ? focusColor : color} />
			</div>
		</Dropdown>
	);
}
